import axios from 'axios';
import applyCaseMiddleware from 'axios-case-converter';
import { HttpClient } from '@/api/HttpClient';
import { IApiContext } from '@/providers/ApiContextProvider/ApiContext.types';
import { AlertingApi } from '@/api/alerting/alerting.api';
import { AnalystNotesApi } from '@/api/analystNotes/analystNotes.api';
import { UserApi } from '@/api/user/user.api';
import { EntityIdsApi } from '@/api/entityIds/entityIds.api';
import { CalculatorsApi } from '@/api/calculators/calculators.api';
import { RisksApi } from '@/api/risks/risks.api';
import { RatingsApi } from '@/api/ratings/ratings.api';
import { DocsApi } from '@/api/docs/docs.api';
import { DocumentationApi } from '@/api/documentation/documentation.api';
import { MonitoringApi } from '@/api/monitoring/monitoring.api';
import { MarketPricesApi } from '@/api/marketPrices/marketPrices.api';
import { StablecoinApi } from '@/api/stablecoin/stablecoin.api';
import { getApiBaseUrl } from '@/api/getApiBaseUrl';
import { isTokenExpired } from '@/hooks/useUserIdFromToken';
import type { TLogoutReason } from '@/providers/AuthProvider/NewAuthProvider.types';

const INVALID_PERMISSIONS_DETAIL = 'Invalid permissions';

export const createApiClient = (
  accessToken: string,
  logOut: (reason?: TLogoutReason) => void,
  onPermissionDenied: () => void,
): IApiContext => {
  const apiBaseUrl = getApiBaseUrl();
  const isSwaProxyEnabled = import.meta.env.VITE_USE_SWA_PROXY === 'true';

  const axiosClient = axios.create({
    baseURL: apiBaseUrl,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  axiosClient.interceptors.request.use(
    config => {
      if (accessToken) {
        if (isTokenExpired(accessToken)) {
          logOut('access_token_expired');
        }
        config.headers['Authorization'] = `Bearer ${accessToken}`;
        if (isSwaProxyEnabled) {
          config.headers['x-forwarded-authorization'] = `Bearer ${accessToken}`;
        } else {
          delete config.headers['x-forwarded-authorization'];
        }
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    },
  );

  axiosClient.interceptors.response.use(
    response => response,
    error => {
      if (error.response?.status === 401) {
        logOut('unauthorized_401');
      }

      if (
        error.response?.status === 403 &&
        error.response.data?.detail === INVALID_PERMISSIONS_DETAIL
      ) {
        onPermissionDenied();
      }

      return Promise.reject(error);
    },
  );

  // middleware converts camelCase to snake_case on request (outgoing),
  // and snake_case to camelCase on response (incoming)
  const axiosInstanceWithApplyCaseMiddleware = applyCaseMiddleware(axiosClient, {
    preservedKeys: [], // example if we want to skip conversion for particular keys
  });

  const httpClient = new HttpClient(axiosInstanceWithApplyCaseMiddleware);

  const apiClient: IApiContext = {
    alertingApi: new AlertingApi(httpClient),
    analystNotesApi: new AnalystNotesApi(httpClient),
    calculatorsApi: new CalculatorsApi(httpClient),
    docsApi: new DocsApi(httpClient),
    documentationApi: new DocumentationApi(httpClient),
    entityIdsApi: new EntityIdsApi(httpClient),
    monitoringApi: new MonitoringApi(httpClient),
    userApi: new UserApi(httpClient),
    ratingsApi: new RatingsApi(httpClient),
    risksApi: new RisksApi(httpClient),
    marketPricesApi: new MarketPricesApi(httpClient),
    stablecoinApi: new StablecoinApi(httpClient),
  };

  return apiClient;
};
